import {useEffect, useState} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CelebrationRoundedIcon from "@mui/icons-material/CelebrationRounded";
import {doc, getDoc, onSnapshot} from "firebase/firestore";
import firebase from "../core/firebase";
import application from "../core/application";
import {AiCreditsSection} from "../settings/components/ai-credits-section";

export const AI_CREDIT_COSTS = {
  extractProductFromImage: 2,
  enhanceProductImage: 3,
  extractPayableFromDocument: 2,
  bulkProductsExtract: 5,
  sellCommand: 1,
} as const;

export class InsufficientAiCreditsError extends Error {
  constructor() {
    super("INSUFFICIENT_AI_CREDITS");
  }
}

export const isInsufficientAiCreditsError = (err: unknown): boolean => {
  if (err instanceof InsufficientAiCreditsError) return true;
  if (!(err instanceof Error)) return false;
  return err.message === "INSUFFICIENT_AI_CREDITS" || err.message.includes("Créditos de IA insuficientes");
};

const AI_CREDITS_REQUIRED_EVENT = "symplou:ai-credits-required";

type AiCreditsRequiredDetail = {
  cost: number;
};

export const notifyInsufficientAiCredits = (cost: number) => {
  window.dispatchEvent(new CustomEvent<AiCreditsRequiredDetail>(AI_CREDITS_REQUIRED_EVENT, {
    detail: {cost},
  }));
};

export const getAiCreditBalance = async (): Promise<number> => {
  const business = application.bussiness.get();
  if (!business) return 0;

  const businessSnap = await getDoc(doc(firebase.db, "bussiness", business.key));
  const subscriptionUid = businessSnap.data()?.subscriptionUid || businessSnap.data()?.ownerUid;
  if (!subscriptionUid || typeof subscriptionUid !== "string") return 0;

  const subscriptionSnap = await getDoc(doc(firebase.db, "customerSubscriptions", subscriptionUid));
  return (subscriptionSnap.data()?.aiCredits as number | undefined) || 0;
};

export const ensureAiCredits = async (cost: number) => {
  const credits = await getAiCreditBalance();
  if (credits >= cost) return;
  notifyInsufficientAiCredits(cost);
  throw new InsufficientAiCreditsError();
};

export const AiCreditsRequiredDialog = () => {
  const [requiredCost, setRequiredCost] = useState<number | null>(null);

  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<AiCreditsRequiredDetail>).detail;
      setRequiredCost(detail?.cost || 1);
    };
    window.addEventListener(AI_CREDITS_REQUIRED_EVENT, listener);
    return () => window.removeEventListener(AI_CREDITS_REQUIRED_EVENT, listener);
  }, []);

  return (
    <Dialog
      open={requiredCost !== null}
      onClose={() => setRequiredCost(null)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{fontWeight: 800}}>Créditos de IA insuficientes</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
            Esta operação consome {requiredCost || 1} crédito(s). Adicione créditos para continuar usando a IA.
          </Alert>
          <AiCreditsSection hideHeader />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const WELCOME_MODAL_STORAGE_KEY = "symplou:ai-welcome-bonus-seen";

export const AiCreditCostHint = ({cost}: {cost: number}) => (
  <Chip
    size="small"
    icon={<AutoAwesomeRoundedIcon sx={{fontSize: 14}} />}
    label={`${cost} crédito${cost > 1 ? "s" : ""}`}
    variant="outlined"
    color="secondary"
    sx={{fontWeight: 700}}
  />
);

export const AiWelcomeBonusDialog = () => {
  const [open, setOpen] = useState(false);
  const [credits, setCredits] = useState(0);

  useEffect(() => {
    const user = application.user.get();
    const business = application.bussiness.get();
    if (!user || !business) return;

    const storageKey = `${WELCOME_MODAL_STORAGE_KEY}:${user.id}:${business.key}`;
    if (localStorage.getItem(storageKey) === "1") return;

    const businessRef = doc(firebase.db, "bussiness", business.key);
    let subUnsub: (() => void) | undefined;

    const unsubBusiness = onSnapshot(businessRef, (businessSnap) => {
      const subscriptionUid = businessSnap.data()?.subscriptionUid || businessSnap.data()?.ownerUid;
      if (!subscriptionUid || typeof subscriptionUid !== "string") return;

      if (subUnsub) subUnsub();
      subUnsub = onSnapshot(doc(firebase.db, "customerSubscriptions", subscriptionUid), (subSnap) => {
        const data = subSnap.data();
        if (!data?.welcomeAiBonusGrantedAt) return;
        const currentCredits = Number(data.aiCredits || 0);
        setCredits(currentCredits);
        setOpen(true);
      });
    });

    return () => {
      unsubBusiness();
      if (subUnsub) subUnsub();
    };
  }, []);

  const close = () => {
    const user = application.user.get();
    const business = application.bussiness.get();
    if (user && business) {
      localStorage.setItem(`${WELCOME_MODAL_STORAGE_KEY}:${user.id}:${business.key}`, "1");
    }
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth PaperProps={{sx: {borderRadius: 3}}}>
      <DialogTitle sx={{pb: 1}}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CelebrationRoundedIcon color="secondary" />
          <Typography variant="h6" fontWeight={800}>Boas-vindas ao IA do Symplou</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            background: "linear-gradient(135deg, rgba(13,148,136,0.10) 0%, rgba(56,189,248,0.08) 100%)",
            border: "1px solid rgba(13,148,136,0.25)",
            mb: 2,
          }}
        >
          <Typography variant="body1" fontWeight={700}>
            Você recebeu R$ 20 em créditos de IA.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
            Saldo atual: <strong>{credits.toLocaleString("pt-BR")} créditos</strong>.
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Use seus créditos para preencher produtos por foto, interpretar planilhas e extrair dados de documentos automaticamente.
        </Typography>
      </DialogContent>
      <DialogActions sx={{px: 3, pb: 2}}>
        <Button variant="contained" onClick={close}>Começar a usar</Button>
      </DialogActions>
    </Dialog>
  );
};
